import { useInfiniteQuery } from '@tanstack/react-query';
import { isAxiosError, isCancel } from 'axios';
import { useTranslation } from 'next-i18next';

import { SearchImagesProvider } from '@/api/gen/_model/searchImagesProvider';
import { searchImages } from '@/api/gen/image-search/image-search.gen';
import { ZepQuizApiError } from '@/api/httpClient';

import type {
  ImageSearchProvider,
  ImageSearchResult,
} from '../types/imageSearch.types';
import { openImageSearchFailDialog } from '../utils/imageSearchFailDialog';
import { getPexelsLocale } from '../utils/pexelsLocale';

// 기존 Vercel 라우트 시절의 페이지 크기를 유지한다 (naver display=20, pexels per_page=50)
const IMAGES_PER_PAGE: Record<ImageSearchProvider, number> = {
  naver: 20,
  pexels: 50,
};

const PROVIDER_PARAM: Record<
  ImageSearchProvider,
  (typeof SearchImagesProvider)[keyof typeof SearchImagesProvider]
> = {
  naver: SearchImagesProvider.naver,
  pexels: SearchImagesProvider.pexels,
};

// 검색 실패 다이얼로그 조건 — 기존 pexels/naver 훅과 동일하게
// 네트워크 오류 · 5xx · 429(rate limit)에서만 노출한다.
export const shouldOpenFailDialog = (error: unknown): boolean => {
  // react-query가 signal로 abort한 취소(CanceledError)는 response가 없어
  // 네트워크 오류와 구분해 제외하지 않으면 스크롤 중 abort마다 다이얼로그가 뜬다.
  if (isCancel(error)) return false;
  if (error instanceof ZepQuizApiError) {
    return error.httpStatus >= 500 || error.httpStatus === 429;
  }
  if (isAxiosError(error)) {
    if (!error.response) return true; // Network error
    return error.response.status >= 500 || error.response.status === 429;
  }
  return false;
};

interface UseImageSearchParams {
  provider: ImageSearchProvider;
  searchQuery: string;
  countryCode: string;
  enabled: boolean;
}

// orval은 infinite query 훅을 생성하지 않아 페이지네이션 래핑만 직접 유지하고,
// fetcher·타입은 생성 코드(searchImages, ImageSearchRes)를 사용한다.
export const useImageSearch = ({
  provider,
  searchQuery,
  countryCode,
  enabled,
}: UseImageSearchParams) => {
  const { t } = useTranslation(['build', 'common']);
  const locale =
    provider === 'pexels' ? getPexelsLocale(countryCode) : undefined;

  return useInfiniteQuery({
    queryKey: ['image-search', provider, searchQuery, locale],
    queryFn: async ({
      pageParam,
      signal,
    }): Promise<ImageSearchResult | undefined> => {
      try {
        const { data } = await searchImages(
          {
            provider: PROVIDER_PARAM[provider],
            query: searchQuery,
            page: pageParam,
            perPage: IMAGES_PER_PAGE[provider],
            locale,
          },
          undefined,
          signal,
        );
        return data;
      } catch (error) {
        if (shouldOpenFailDialog(error)) {
          openImageSearchFailDialog(t);
          return undefined;
        }
        throw error;
      }
    },
    initialPageParam: 1,
    getNextPageParam: lastPage => lastPage?.nextPage ?? undefined,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000), // 1s, 2s, 4s로 지수 백오프
    enabled: enabled && !!searchQuery.trim(),
  });
};
